<template>
	<div>
		<SelectionModal
			:confirmText="confirmButtonText"
			:cancelText="cancelText || $t('common_button_cancel')"
			:confirmProps="{ loading }"
			:items="users"
			:columns="columns"
			:small="small"
			:large="large"
			:selectedItemsById="selectedUsersById"
			:loading="fetchingUsers"
			:loadingMore="loadingMore"
			:startingCount="startingCount"
			:sortItems="sortUsersByFullname"
			:maxLimit="maxLimit"
			class="add-members"
			@selection="onSelectionChange"
			@prevented="onSelectionPrevented"
			@load-more="loadMore"
			@confirm="addMembersToGroup"
			@cancel="onCancel"
			@close="$attrs.onClose"
			ref="modal"
		>
			<template #title>
				<div class="modal-title-content">
					<h1
						class="modal-title"
						v-text="$t('admin_groups_add_members_action')"
					/>
					<div class="member-count-container">
						<UserIcon type="regular" />
						{{ totalCount }}/{{ maxLimit }}
					</div>
				</div>
			</template>
			<template #cell="{ data, selected }">
				<div class="user-content" v-if="data.id !== 'invitelink'">
					<UserCell
						class="user-info"
						:key="data.id"
						:user="data"
						hideTag
						hideContact
					/>
					<div
						class="already-selected"
						v-if="selected === SELECTION_FIXED"
						v-t="'admin_groups_already_group_member_label'"
					/>
				</div>
				<RouterLink
					v-if="data.id === 'invitelink'"
					class="invite-link"
					:to="toInviteLink"
				>
					<LinkIcon />
					{{ $t('groupinvites_invite_with_link') }}
				</RouterLink>
			</template>
			<template #filters>
				<div class="filters">
					<Input
						:placeholder="$t('admin_people_search_action')"
						class="search"
						v-model="query"
						ref="searchInput"
					/>
					<OrgUserFilter @change="updateFilter" />
				</div>
			</template>
			<template #footer-max>
				<span
					class="at-max-limit"
					v-text="$t('admin_people_add_person_max_limit')"
				/>
			</template>
			<template #footer-unselected>
				<slot name="footer-unselected" />
			</template>
		</SelectionModal>

		<!-- MAX ALERT MODAL -->
		<StandardModal
			v-if="showMaxAlert"
			open
			small
			hideCancel
			:title="$t('common_max_limit')"
			:confirmText="$t('common_button_close')"
			:confirmProps="{ danger: true }"
			@cancel="showMaxAlert = false"
			@confirm="showMaxAlert = false"
			class="max-modal"
			ref="maxModal"
		>
			<div class="max-modal-content">
				<CircleExclamationIcon halo />
				<div class="help" v-t="'admin_people_add_person_max_limit'" />
			</div>
		</StandardModal>
	</div>
</template>

<script>
import { getMyUser, sortUsersByFullname } from '@/store'
import { Table, Input, StandardModal } from '@/components/common'
import {
	get,
	SelectionModal,
	UserCell,
} from '@/components/OrgManagement/common'
import { UserIcon, CircleExclamationIcon, LinkIcon } from '@/icons'
import debounce from 'debounce'
import OrgUserFilter from './OrgUserFilter'

const PAGE_SIZE = 30
const INVITE_LINK = { id: 'invitelink' }

export default {
	name: 'AddMembersModal',
	components: {
		Input,
		SelectionModal,
		UserCell,
		StandardModal,
		CircleExclamationIcon,
		UserIcon,
		LinkIcon,
		OrgUserFilter,
	},
	inheritAttrs: false,
	emits: ['cancel', 'add'],

	props: {
		group: Object,
		loading: Boolean,
		small: Boolean,
		large: Boolean,
		initialUsers: Array,
		confirmText: {
			type: String,
			default: '',
		},
		cancelText: {
			type: String,
			default: '',
		},
		maxLimit: {
			type: Number,
			default: 0,
		},
		startingCount: {
			type: Number,
			default: 0,
		},
	},

	data() {
		const me = getMyUser()
		return {
			columns: [{ field: 'fullname' }],
			users: [],
			selectedUsersById: {},
			fetchingUsers: false,
			loadingMore: false,
			showMaxAlert: false,
			query: null,
			bulkErrors: null,
			SELECTION_FIXED: Table.SELECTION_FIXED,
			filter: me.isAdmin ? 'all' : 'connections',
			sortUsersByFullname,
		}
	},

	created() {
		this.fetchUsersDebounced = debounce(this.fetchUsers.bind(this), 500)
		this.fixedSelectionsById = {}
	},

	async mounted() {
		await this.fetchUsers()

		if (this.initialUsers?.length) {
			// if there are initial selections, update the selectedUsersById
			this.selectedUsersById = this.initialUsers.reduce((acc, user) => {
				acc[user.id] = { item: user, selected: true }
				return acc
			}, {})
		}
	},

	watch: {
		query() {
			this.fetchingUsers = true
			this.fetchUsersDebounced()
		},
	},

	computed: {
		selectedCount() {
			return Object.values(this.selectedUsersById).filter(
				(x) => x.selected === true
			).length
		},
		totalCount() {
			return this.startingCount + this.selectedCount
		},
		confirmButtonText() {
			if (this.confirmText) return this.confirmText
			if (this.selectedCount === 0)
				return this.$t('admin_groups_add_people_button')
			return this.$t(
				'admin_groups_add_people_button_count.variable',
				{
					Count: this.selectedCount,
				},
				this.selectedCount
			)
		},
		toInviteLink() {
			if (!this.group) return {}
			return {
				name: 'groupinvites',
				params: { id: this.group.id },
				query: { new: true },
			}
		},
	},

	methods: {
		onCancel() {
			this.bulkErrors = null
			this.$emit('cancel')
		},

		onSelectionPrevented() {
			this.showMaxAlert = true
		},

		onSelectionChange(selected = {}) {
			this.selectedUsersById = Object.assign(
				{},
				selected,
				this.fixedSelectionsById
			)
			setTimeout(() => {
				this.query = null
				this.$refs.searchInput?.focus()
			}, 100)
		},

		setFixedSelections() {
			const { users_with_chat = [], data: users = [] } = this.apiResponse
			this.fixedSelectionsById = users_with_chat
				.map((id) => users.find((u) => u.id === id))
				.filter((u) => u)
				.reduce((acc, u) => {
					acc[u.id] = { item: u, selected: Table.SELECTION_FIXED }
					return acc
				}, this.fixedSelectionsById)
			this.selectedUsersById = Object.assign(
				{},
				{
					[INVITE_LINK.id]: {
						item: INVITE_LINK,
						selected: Table.SELECTION_FIXED,
					},
				},
				this.selectedUsersById,
				this.fixedSelectionsById
			)
		},

		async addMembersToGroup() {
			const users = Object.values(this.selectedUsersById)
				.filter((x) => x.selected === true)
				.map((x) => x.item)
			this.$emit('add', users)
			this.onCancel()
		},

		async fetchUsers() {
			this.fetchingUsers = true
			const params = { 'page[size]': PAGE_SIZE }
			if (this.group) {
				params['additional[memberships.chat_id]'] = this.group.id
			}
			if (this.filter === 'all') {
				params['filter[relation]'] = 'all'
			} else if (this.filter === 'connections') {
				params['filter[relation]'] = 'connections'
			} else if (Number.isInteger(this.filter)) {
				params['filter[bubble_ids][]'] = this.filter
			}
			if (this.query) {
				params['filter[query]'] = this.query
			}

			this.apiResponse = await get('/users/search', params)
			const filteredUsers = this.apiResponse.data.filter(
				(user) => user.role !== 'support'
			)
			if (this.group) {
				this.users = [INVITE_LINK].concat(filteredUsers)
			} else {
				this.users = filteredUsers
			}

			this.setFixedSelections()
			this.fetchingUsers = false
			this.$refs.modal.$refs.mainTable.scrollToTop()
		},

		async loadMore() {
			if (!this.apiResponse?.next || this.loadingMore) return
			try {
				this.loadingMore = true
				this.apiResponse = await this.apiResponse.next()
				this.users = this.users.concat(this.apiResponse.data)
				this.setFixedSelections()
			} catch (e) {
				console.error(e)
			}

			await this.$nextTick()
			this.loadingMore = false
		},

		updateFilter(value) {
			this.filter = value
			this.fetchUsers()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.filters {
	width: 100%;
	@include flex(16px);
}
.user-content {
	@include flex-spread;
	width: 100%;
	.user-info {
		flex-shrink: 0;
		flex-grow: 1;
		@include ellipsis;
	}
	.already-selected {
		font-style: italic;
		flex-shrink: 0;
		margin-left: 8px;
		&::after {
			// last letter in italic word was getting cut off :(
			content: ' ';
			margin-right: 4px;
		}
	}
}
.at-max-limit {
	margin-left: 24px;
	max-width: 400px;
	@include ellipsis;
	font-weight: normal;
	color: var(--neutral-400);
}
.modal-title-content {
	position: relative;
	.modal-title {
		font-size: 26px;
		@include fontFamily(900);
	}
	.member-count-container {
		@include flex(8px);
		position: absolute;
		bottom: -14px;
		left: 0;
		height: 13px;
		font-size: 13px;
		color: var(--neutral-600);
	}
}
.max-modal-content {
	height: 100%;
	@include flex-column-center;
	justify-content: space-evenly;
	.pronto-icon {
		font-size: 55px;
		color: var(--red-200);
	}
}
.invite-link {
	@include flex(8px);
	color: var(--blue-300);
	text-decoration: none;
	.pronto-icon {
		margin-right: 8px;
		font-size: 23px;
	}
}
</style>

<style lang="scss" scoped>
@import 'src/sass/variables';

.modal-content {
	.table-cell-content {
		width: 100%;
	}
	.table-row-invitelink {
		.table-col-0 {
			margin-left: -40px;
			.table-cell-content {
				opacity: 1 !important;
			}
		}
	}
}
</style>
